import { Inject, Injectable } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { Meta, Title } from '@angular/platform-browser'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

// per-page <title>, description, open graph / twitter tags, canonical link, robots, and
// structured data (json-ld). AppComponent applies route `data` (title, description, noindex)
// on every NavigationEnd; detail pages (work/:slug, services/:slug, collections/:slug) call
// update() and setStructuredData() once their contentful entry has loaded.

export interface SeoData {
  title?: string        // page title, without the site name
  description?: string
  image?: string        // absolute url
  path?: string         // canonical path, e.g. /work/sfo-branding
  noindex?: boolean
  breadcrumbs?: [string, string][] // static nested routes, e.g. [['collections', '/collections'], ['logos', '/collections/logos']]
}

export const SITE_URL = 'https://lowercaseproductions.com'
export const SITE_NAME = 'lowercase productions'
export const SITE_DESCRIPTION = 'lowercase productions is a prolific, San Francisco-based visual design and communications agency transforming ideas into standout solutions.'
const DEFAULT_TITLE = 'lowercase productions | San Francisco visual design & communications agency'
// lcp logo, padded onto white (it is square, so fit=pad instead of cropping); keep in sync with og:image in index.html
const DEFAULT_IMAGE = 'https://images.ctfassets.net/ertyhukhlvrl/aElUTxT1HW5DtmfakNmTi/f5b7e84a6ed24f634e3800f6c590b0b0/lcp_logo_figma.png?w=1200&h=630&fit=pad&bg=rgb:ffffff&fm=jpg&q=80'
const DESCRIPTION_MAX = 160
const LOGO_URL = DEFAULT_IMAGE.split('?')[0] // the square logo itself, for structured data

// organization facts for structured data; only what the site itself publishes (footer)
const ORGANIZATION_ID = SITE_URL + '/#organization'
const ORGANIZATION_EMAIL = 'info@lowercaseproductions.com'
const ORGANIZATION_PHONE = '+1-415-994-6653'
const ORGANIZATION_SAME_AS: string[] = [] // official social profile urls (linkedin, instagram, ...)

@Injectable({ providedIn: 'root' })
export class SeoService {

  private current: SeoData = {}

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private title: Title
  ) {}

  // replaces everything; missing fields fall back to the site defaults.
  // structured data is reset too: the home page gets the organization, other pages none until
  // they add their own
  set(data: SeoData) {
    this.current = {}
    this.update(data)
    let isHome = !data.path || data.path === '/'
    let nodes = data.noindex ? []
      : isHome ? [SeoService.organization(), SeoService.website()]
      : data.breadcrumbs ? [SeoService.breadcrumbs(data.breadcrumbs)]
      : []
    this.setStructuredData(nodes)
  }

  // canonical url of the current page
  url() {
    let path = this.current.path
    return SITE_URL + (path && path !== '/' ? path : '/')
  }

  // json-ld in <head> (one script, a schema.org @graph); [] removes it
  setStructuredData(nodes: object[]) {
    let script = this.document.head.querySelector<HTMLScriptElement>('script#structured-data')
    if (!nodes.length) return script?.remove()
    if (!script) {
      script = this.document.createElement('script')
      script.id = 'structured-data'
      script.type = 'application/ld+json'
      this.document.head.appendChild(script)
    }
    // escape < so content can never close the script tag
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': nodes }).replace(/</g, '\\u003c')
  }

  // merges into what's already set (e.g. a detail page adding its title to the route's path)
  update(data: SeoData) {
    this.current = Object.assign({}, this.current, data)
    let { title, description, image, path, noindex } = this.current

    let fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
    description = SeoService.truncate(description || SITE_DESCRIPTION)
    image = image || DEFAULT_IMAGE
    let url = SITE_URL + (path && path !== '/' ? path : '/')

    this.title.setTitle(fullTitle)
    this.setTag('name', 'description', description)
    this.setTag('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow')

    this.setTag('property', 'og:site_name', SITE_NAME)
    this.setTag('property', 'og:type', 'website')
    this.setTag('property', 'og:title', fullTitle)
    this.setTag('property', 'og:description', description)
    this.setTag('property', 'og:image', image)
    this.setTag('property', 'og:url', url)

    this.setTag('name', 'twitter:card', 'summary_large_image')
    this.setTag('name', 'twitter:title', fullTitle)
    this.setTag('name', 'twitter:description', description)
    this.setTag('name', 'twitter:image', image)

    this.setCanonical(noindex ? null : url)
  }

  private setTag(attr: 'name' | 'property', key: string, content: string) {
    this.meta.updateTag({ [attr]: key, content }, `${attr}="${key}"`)
  }

  private setCanonical(url: string | null) {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!url) return link?.remove()
    if (!link) {
      link = this.document.createElement('link')
      link.setAttribute('rel', 'canonical')
      this.document.head.appendChild(link)
    }
    link.setAttribute('href', url)
  }

  //
  // structured data (schema.org) nodes
  //

  static organization() {
    return {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: SITE_NAME,
      alternateName: 'lcp',
      url: SITE_URL + '/',
      logo: LOGO_URL,
      image: LOGO_URL,
      description: SITE_DESCRIPTION,
      email: ORGANIZATION_EMAIL,
      telephone: ORGANIZATION_PHONE,
      address: { '@type': 'PostalAddress', addressLocality: 'San Francisco', addressRegion: 'CA', addressCountry: 'US' },
      ...(ORGANIZATION_SAME_AS.length ? { sameAs: ORGANIZATION_SAME_AS } : {}),
    }
  }

  static website() {
    return {
      '@type': 'WebSite',
      '@id': SITE_URL + '/#website',
      url: SITE_URL + '/',
      name: SITE_NAME,
      publisher: { '@id': ORGANIZATION_ID },
    }
  }

  // [['work', '/work'], ['San Francisco Botanical Garden', '/work/gggp']] => home > work > ...
  static breadcrumbs(trail: [string, string][]) {
    let items = [['home', '/'] as [string, string], ...trail]
    return {
      '@type': 'BreadcrumbList',
      itemListElement: items.map(([name, path], i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name,
        item: SITE_URL + (path === '/' ? '/' : path),
      })),
    }
  }

  // reference to the organization node (defined in full on the home page)
  static organizationRef() {
    return { '@id': ORGANIZATION_ID }
  }

  //
  // helpers for building SeoData from contentful entries
  //

  // first non-empty candidate (strings or rich text documents), skipping lorem ipsum placeholders
  static text(...candidates: any[]): string {
    for (let candidate of candidates) {
      if (!candidate) continue
      let text = typeof candidate === 'string' ? candidate : documentToPlainTextString(candidate)
      text = text.replace(/\s+/g, ' ').trim()
      if (text && !/lorem ipsum/i.test(text)) return text
    }
    return ''
  }

  // 1200x630 jpg of the first image asset, via the contentful images api
  static image(media: any[] | undefined): string | undefined {
    let asset = (media || []).find(m => m?.fields?.file?.contentType?.startsWith('image/'))
    if (!asset) return
    return 'https:' + asset.fields.file.url + '?w=1200&h=630&fit=fill&fm=jpg&q=80'
  }

  static truncate(text: string, max = DESCRIPTION_MAX): string {
    if (text.length <= max) return text
    let cut = text.slice(0, max - 1)
    return cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:.]+$/, '') + '…'
  }
}
